import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, track } from "../lib/api";
import { useFlow } from "../context/FlowContext";
import { LotusMark, PaisleyDivider } from "../components/Motif";
import { ArrowRight, ShieldCheck, Sparkles, Brain, ScrollText } from "lucide-react";

export default function Landing() {
  const navigate = useNavigate();
  const { update } = useFlow();
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    track("homepage_view");
    api.get("/catalog").then(({ data }) => setCategories(data.categories)).catch(() => {});
  }, []);

  const pickCategory = (cat) => {
    track("question_selected", { category: cat.id });
    update({ category: cat.id });
    navigate(`/q/${cat.id}`);
  };

  return (
    <div className="jaanlo-grain">
      <section className="mx-auto max-w-6xl px-5 pt-10 md:pt-16">
        <div className="max-w-2xl fade-up">
          <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1 text-xs text-muted-foreground">
            <LotusMark className="h-3.5 w-3.5 gold-text" /> Personalized insight, not a horoscope
          </div>
          <h1 className="font-display text-4xl font-semibold leading-[1.1] tracking-tight sm:text-5xl lg:text-6xl" data-testid="landing-headline">
            Aap kya jaana<br />chahte hain?
          </h1>
          <p className="mt-5 max-w-xl text-base text-muted-foreground md:text-lg">
            Personalized insights based on your answers, birth details and traditional systems.
          </p>
        </div>

        <div className="mt-10 grid grid-cols-2 gap-4 md:mt-12 md:grid-cols-3 md:gap-6">
          {categories.map((cat, i) => (
            <button
              key={cat.id}
              data-testid={`category-card-${cat.id}`}
              onClick={() => pickCategory(cat)}
              style={{ animationDelay: `${i * 70}ms` }}
              className="card-soft group fade-up flex flex-col items-start gap-3 rounded-2xl p-5 text-left btn-lift md:p-7"
            >
              <span className="text-3xl md:text-4xl">
                {cat.id === "paisa"
                  ? <span className="font-display gold-text">₹</span>
                  : cat.emoji}
              </span>
              <span className="font-display text-lg font-semibold md:text-xl">{cat.title}</span>
              <span className="text-sm text-muted-foreground">{cat.question}</span>
              <span className="mt-1 inline-flex items-center gap-1 text-sm font-medium gold-text opacity-0 transition-opacity group-hover:opacity-100">
                Explore <ArrowRight className="h-3.5 w-3.5" />
              </span>
            </button>
          ))}
        </div>

        <div className="mt-8 flex justify-center">
          <button
            data-testid="explore-all-tools-btn"
            onClick={() => navigate("/explore")}
            className="rounded-full border border-border bg-card px-6 py-2.5 text-sm font-medium btn-lift"
          >
            Explore all tools
          </button>
        </div>
      </section>

      {/* How this reading works */}
      <section className="mx-auto mt-16 max-w-6xl px-5 md:mt-24">
        <PaisleyDivider className="mb-10" />
        <h2 className="font-display text-2xl font-semibold tracking-tight md:text-3xl">How this reading works</h2>
        <div className="mt-6 grid gap-4 md:grid-cols-4">
          {[
            { icon: ScrollText, t: "Your details", d: "Your birth details and answers form your personal profile." },
            { icon: Sparkles, t: "Calculations", d: "Relevant traditional calculations are performed." },
            { icon: Brain, t: "Interpretation", d: "Traditional interpretations are identified for your chart." },
            { icon: ShieldCheck, t: "Plain language", d: "AI converts those interpretations into understandable language." },
          ].map((s, i) => {
            const Icon = s.icon;
            return (
              <div key={i} className="card-soft rounded-2xl p-6">
                <Icon className="h-5 w-5 gold-text" />
                <div className="mt-3 font-display text-lg font-semibold">{s.t}</div>
                <p className="mt-1 text-sm text-muted-foreground">{s.d}</p>
              </div>
            );
          })}
        </div>
        <p className="mt-6 max-w-3xl text-xs text-muted-foreground">
          Jaanlo offers traditional and spiritual interpretations for personal reflection. These are not
          scientifically validated and do not guarantee any outcome.
        </p>
      </section>

      <footer className="mx-auto mt-16 max-w-6xl px-5 pb-10 text-xs text-muted-foreground">
        <div className="flex flex-wrap gap-4 border-t border-border pt-6">
          <button onClick={() => navigate("/about")} className="hover:text-foreground">About</button>
          <button onClick={() => navigate("/privacy")} className="hover:text-foreground">Privacy</button>
          <button onClick={() => navigate("/terms")} className="hover:text-foreground">Terms</button>
          <button onClick={() => navigate("/login")} className="hover:text-foreground">Sign in</button>
        </div>
      </footer>
    </div>
  );
}
